import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonBadge,
  IonText,
  IonRefresher,
  IonRefresherContent,
  IonFab,
  IonFabButton,
  IonAlert,
  IonToast,
  IonLoading
} from '@ionic/react';
import {
  peopleOutline,
  statsChartOutline,
  scanOutline
} from 'ionicons/icons';
import { useHistory } from 'react-router';
import NetworkingService, { ExistingConnectionError, UserNotFoundError, SelfScanError } from '../services/NetworkingService';
import { NetworkingStats, NetworkingProfile } from '../models/Networking';
import { isFeatureEnabled } from '../config/app.config';
import { isOnline, OFFLINE_RESTRICTED_ACTIONS } from '../utils/offlineManager';
import QrScanner from '../components/QrScanner';
import NetworkingContactModal from '../components/NetworkingContactModal';
import OfflineNotification from '../components/OfflineNotification';
import { connect } from '../data/connect';
import './NetworkingPage.css';

interface StateProps {
  username: string;
  userced: string;
  eventcode: string;
}

interface DispatchProps {}

interface NetworkingPageProps extends StateProps, DispatchProps {}

const NetworkingPage: React.FC<NetworkingPageProps> = ({ username, userced, eventcode }) => {
  const history = useHistory();
  const [stats, setStats] = useState<NetworkingStats>({
    totalScans: 0,
    uniqueContacts: 0,
    topCompanies: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'success' | 'warning' | 'danger'>('success');

  // Estados para alertas
  const [showAlert, setShowAlert] = useState(false);
  const [alertHeader, setAlertHeader] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertButtons, setAlertButtons] = useState<any[]>([]);

  // Estado para evitar procesamiento múltiple del mismo QR
  const [lastProcessedQR, setLastProcessedQR] = useState<string>('');

  // Estado para controlar el cierre del modal
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [hasProfile, setHasProfile] = useState(true);

  // Estados para el escáner QR
  const [showScanner, setShowScanner] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [networkingContact, setNetworkingContact] = useState<NetworkingProfile | null>(null);
  const [showNetworkingContact, setShowNetworkingContact] = useState(false);

  // Estados para notificaciones offline
  const [showOfflineNotification, setShowOfflineNotification] = useState(false);
  const [offlineAction, setOfflineAction] = useState<string>('');

  useEffect(() => {
    // Verificar si la funcionalidad está habilitada
    if (!isFeatureEnabled('networking')) {
      history.replace('/tabs/schedule');
      return;
    }

    loadStats();
  }, [history]);

  const loadStats = async () => {
    try {
      setIsLoading(true);
      const networkingService = NetworkingService.getInstance();

      // Verificar si hay perfil de networking disponible
      const currentProfile = await networkingService.getCurrentProfile();
      if (!currentProfile) {
        console.warn('NetworkingPage: No hay perfil de networking disponible');
        setHasProfile(false);
        // Establecer estadísticas vacías en lugar de mostrar error
        setStats({
          totalScans: 0,
          uniqueContacts: 0,
          topCompanies: []
        });
        return;
      }

      setHasProfile(true);

      const networkingStats = await networkingService.getNetworkingStats();
      setStats(networkingStats);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      // Solo mostrar error si no es un problema de perfil faltante
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (!errorMessage.includes('perfil de networking')) {
        setToastMessage('Error al cargar estadísticas de networking');
        setShowToast(true);
      }
      // Establecer estadísticas vacías como fallback
      setStats({
        totalScans: 0,
        uniqueContacts: 0,
        topCompanies: []
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async (event: CustomEvent) => {
    await loadStats();
    event.detail.complete();
  };

  const handleScanClick = () => {
    console.log('NetworkingPage: Activando escáner QR para networking');

    // Verificar si hay conexión a internet
    if (isOnline()) {
      setShowScanner(true);
    } else {
      // Mostrar notificación de que esta acción requiere conexión
      setOfflineAction(OFFLINE_RESTRICTED_ACTIONS.SCAN_QR);
      setShowOfflineNotification(true);
      console.log('NetworkingPage: No se puede escanear QR sin conexión a internet');
    }
  };

  const handleScanSuccess = async (qrContent: string) => {
    console.log('NetworkingPage: QR escaneado:', qrContent);

    // Evitar procesar el mismo QR múltiples veces
    if (qrContent === lastProcessedQR) {
      console.log('NetworkingPage: QR ya procesado, ignorando duplicado');
      return;
    }

    setLastProcessedQR(qrContent);

    // Limpiar el QR procesado después de 3 segundos para permitir re-escaneo
    setTimeout(() => {
      setLastProcessedQR('');
    }, 3000);

    // Cerrar el escáner
    setShowScanner(false);

    // Procesar el QR como networking
    await handleNetworkingQR(qrContent);
  };

  const handleNetworkingQR = async (qrContent: string) => {
    try {
      setIsProcessing(true);
      setToastMessage('Procesando contacto...');
      setToastColor('warning');
      setShowToast(true);

      const networkingService = NetworkingService.getInstance();
      const scannedProfile = await networkingService.processScannedQR(qrContent);

      console.log('NetworkingPage: Perfil escaneado recibido:', {
        id: scannedProfile.id,
        name: scannedProfile.name,
        email: scannedProfile.email,
        phone: scannedProfile.phone,
        company: scannedProfile.company,
        position: scannedProfile.position
      });

      // Mostrar información del contacto escaneado
      setNetworkingContact(scannedProfile);
      setShowNetworkingContact(true);
      setToastMessage(`¡Contacto agregado! ${scannedProfile.name}`);
      setToastColor('success');
      setShowToast(true);

      // Recargar estadísticas
      await loadStats();

      console.log('NetworkingPage: Contacto de networking agregado:', scannedProfile);
    } catch (error) {
      console.error('NetworkingPage: Error al procesar QR de networking:', error);

      if (error instanceof ExistingConnectionError) {
        // Mostrar alerta para conexión existente
        const existingProfile = error.existingProfile;
        setAlertHeader('Conexión Existente');
        setAlertMessage(`Ya tienes una conexión con ${existingProfile.name}. ¿Deseas ver su información?`);
        setAlertButtons([
          {
            text: 'Cancelar',
            role: 'cancel'
          },
          {
            text: 'Ver Contacto',
            handler: () => {
              setNetworkingContact(existingProfile);
              setShowNetworkingContact(true);
            }
          }
        ]);
        setShowAlert(true);
      } else if (error instanceof SelfScanError) {
        // Mostrar alerta para auto-escaneo
        setAlertHeader('Auto-escaneo Detectado');
        setAlertMessage('No puedes escanearte a ti mismo. Por favor, escanea el código QR de otro participante para crear una conexión de networking.');
        setAlertButtons([
          {
            text: 'Entendido',
            role: 'cancel'
          }
        ]);
        setShowAlert(true);
      } else if (error instanceof UserNotFoundError) {
        // Mostrar alerta para usuario no encontrado
        const cedula = error.cedula;
        setAlertHeader('Usuario No Encontrado');
        setAlertMessage(`No se pudo encontrar información para la cédula ${cedula}. Verifica que el código QR sea válido.`);
        setAlertButtons([
          {
            text: 'OK',
            role: 'cancel'
          }
        ]);
        setShowAlert(true);
      } else {
        // Error genérico
        let errorMessage = 'Error al procesar el contacto';
        if (error instanceof Error) {
          errorMessage = error.message;
        }

        setToastMessage(errorMessage);
        setToastColor('danger');
        setShowToast(true);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleScannerClose = () => {
    console.log('NetworkingPage: Cerrando escáner QR');
    setShowScanner(false);
  };

  // Función de debug para probar el mapeo de campos
  const debugFieldMapping = async () => {
    try {
      const networkingService = NetworkingService.getInstance();

      // Probar con una cédula específica (puedes cambiar este valor)
      const testCedula = '1234567890'; // Cambia por una cédula real para probar

      console.log('=== INICIANDO DEBUG DE MAPEO ===');
      await (networkingService as any).debugUserLookup(testCedula);
    } catch (error) {
      console.error('Error en debug:', error);
    }
  };

  // Función de debug específica para el usuario problemático
  const debugProblematicUser = async () => {
    try {
      const networkingService = NetworkingService.getInstance();
      await (networkingService as any).debugProblematicUser();
    } catch (error) {
      console.error('Error en debug problemático:', error);
    }
  };

  // Función para limpiar y recrear usuario problemático
  const cleanAndRecreateUser = async (cedula: string) => {
    try {
      const networkingService = NetworkingService.getInstance();
      await (networkingService as any).cleanAndRecreateUser(cedula);
    } catch (error) {
      console.error('Error en limpieza y recreación:', error);
    }
  };

  // Función para encontrar todos los usuarios con una cédula
  const findAllUsersWithCedula = async (cedula: string) => {
    try {
      const networkingService = NetworkingService.getInstance();
      await (networkingService as any).findAllUsersWithCedula(cedula);
    } catch (error) {
      console.error('Error en búsqueda de usuarios:', error);
    }
  };

  // Función para debug completo de datos de usuario
  const debugUserData = async (cedula: string) => {
    try {
      const networkingService = NetworkingService.getInstance();
      await (networkingService as any).debugUserData(cedula);
    } catch (error) {
      console.error('Error en debug de datos de usuario:', error);
    }
  };

  // Función para limpiar todos los usuarios corruptos
  const cleanAllCorruptedUsers = async () => {
    try {
      const networkingService = NetworkingService.getInstance();
      await (networkingService as any).cleanAllCorruptedUsers();
    } catch (error) {
      console.error('Error en limpieza de usuarios corruptos:', error);
    }
  };

  // Exponer funciones de debug en window para poder llamarlas desde la consola
  (window as any).debugNetworkingMapping = debugFieldMapping;
  (window as any).debugProblematicUser = debugProblematicUser;
  (window as any).cleanAndRecreateUser = cleanAndRecreateUser;
  (window as any).findAllUsersWithCedula = findAllUsersWithCedula;
  (window as any).debugUserData = debugUserData;
  (window as any).cleanAllCorruptedUsers = cleanAllCorruptedUsers;

  const handleContactModalClose = () => {
    if (isModalClosing) {
      console.log('NetworkingPage: Modal ya se está cerrando, ignorando');
      return;
    }

    console.log('NetworkingPage: Cerrando modal de contacto');
    setIsModalClosing(true);

    // Cerrar el modal inmediatamente
    setShowNetworkingContact(false);

    // Limpiar el estado después de que la animación termine
    setTimeout(() => {
      setNetworkingContact(null);
      setIsModalClosing(false);
    }, 300); // Tiempo suficiente para la animación de cierre
  };

  const navigateToContacts = () => {
    history.push('/tabs/networking/contacts');
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Networking</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">Networking</IonTitle>
          </IonToolbar>
        </IonHeader>

        <div className="networking-container">
          {/* Estadísticas */}
          <IonCard className="stats-card">
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={statsChartOutline} />
                Mis Estadísticas
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonGrid>
                <IonRow>
                  <IonCol size="6" className="stat-item">
                    <div className="stat-number">{stats.totalScans}</div>
                    <div className="stat-label">Contactos</div>
                  </IonCol>
                  <IonCol size="6" className="stat-item">
                    <div className="stat-number">{stats.uniqueContacts}</div>
                    <div className="stat-label">Únicos</div>
                  </IonCol>
                </IonRow>
              </IonGrid>

              {stats.topCompanies.length > 0 && (
                <div className="top-companies">
                  <IonText color="medium">
                    <h3>Empresas más frecuentes:</h3>
                  </IonText>
                  {stats.topCompanies.slice(0, 3).map((company, index) => (
                    <div key={company.company} className="company-item">
                      <span>{company.company}</span>
                      <IonBadge color="primary">{company.count}</IonBadge>
                    </div>
                  ))}
                </div>
              )}
            </IonCardContent>
          </IonCard>

          {/* Acciones principales */}
          <IonGrid>
            <IonRow>
              <IonCol size="6">
                <IonCard button onClick={handleScanClick} className="action-card scan-card">
                  <IonCardContent className="action-content">
                    <IonIcon icon={scanOutline} size="large" />
                    <h3>Escanear QR</h3>
                    <p>Conecta con otros participantes</p>
                  </IonCardContent>
                </IonCard>
              </IonCol>
              <IonCol size="6">
                <IonCard button onClick={navigateToContacts} className="action-card contacts-card">
                  <IonCardContent className="action-content">
                    <IonIcon icon={peopleOutline} size="large" />
                    <h3>Contactos</h3>
                    <p>Ver personas escaneadas</p>
                    {stats.totalScans > 0 && (
                      <IonBadge color="success">{stats.totalScans}</IonBadge>
                    )}
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
          </IonGrid>

          {/* Información adicional */}
          <IonCard className="info-card">
            <IonCardContent>
              <IonText color="medium">
                <h3>¿Cómo funciona?</h3>
                <p>
                  1. <strong>Escanea</strong> el código QR de otros participantes<br/>
                  2. <strong>Guarda</strong> automáticamente su información de contacto<br/>
                  3. <strong>Revisa</strong> tu lista de contactos cuando quieras
                </p>
              </IonText>
            </IonCardContent>
          </IonCard>
        </div>

        {/* FAB para escanear rápido */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={handleScanClick} color="primary">
            <IonIcon icon={scanOutline} />
          </IonFabButton>
        </IonFab>

        {/* Alertas y toasts */}
        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header="Información"
          message={alertMessage}
          buttons={['OK']}
        />

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="bottom"
          color={toastColor}
        />

        {/* Componente de escáner QR */}
        <QrScanner
          isOpen={showScanner}
          onClose={handleScannerClose}
          onScanSuccess={handleScanSuccess}
          sendAttendance={false}
          title="Escáner de Networking"
        />

        {/* Modal para mostrar contacto de networking */}
        <NetworkingContactModal
          isOpen={showNetworkingContact && networkingContact !== null && !isModalClosing}
          onClose={handleContactModalClose}
          contact={networkingContact}
        />

        {/* Notificación para acciones que requieren conexión */}
        <OfflineNotification
          isOpen={showOfflineNotification}
          onDidDismiss={() => setShowOfflineNotification(false)}
          actionName={offlineAction}
          duration={3000}
        />

        {/* Loading indicator para procesamiento */}
        <IonLoading
          isOpen={isProcessing}
          message="Procesando contacto..."
          spinner="crescent"
        />

        {/* Alert para errores específicos */}
        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header={alertHeader}
          message={alertMessage}
          buttons={alertButtons}
        />
      </IonContent>
    </IonPage>
  );
};

export default connect<{}, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    username: state.user.username,
    userced: state.user.userced,
    eventcode: state.user.eventcode
  }),
  component: NetworkingPage
});
